import {
  fracOfTime,
  nearestFrameIndexByTime,
  computeMajorTicks,
  positionMajorTicks,
  formatHistoryLabel,
  formatScrubberValueText,
  type PositionedTick
} from "../lib/timeline";
import {
  formatTimeForMode,
  formatOppositeZoneTooltip,
  timezoneOffsetMinutesForMode,
  type TimezoneMode
} from "../lib/formatting";

export interface PlaybackBarOptions {
  onTogglePlay: () => void;
  onScrub: (index: number) => void;
  /** User clicked the 1×/2× toggle; caller owns the actual speed value and calls setSpeed() back. */
  onToggleSpeed: () => void;
  /** User clicked the LOK/UTC toggle; caller owns the actual mode and calls setTimezoneMode() back. */
  onToggleTimezone: () => void;
}

/**
 * The floating (desktop) / docked (mobile) playback card: play/pause, speed
 * and timezone toggles, the "Instrument" timeline ruler (DESIGN.md Aneks
 * M002 §A1-§A2, §A7) and the current frame's timestamp. Structure per
 * DESIGN.md §4-§7, restyled per the Aneks.
 */
export class PlaybackBar {
  readonly element: HTMLElement;
  private readonly playButton: HTMLButtonElement;
  private readonly speedButton: HTMLButtonElement;
  private readonly tzButton: HTMLButtonElement;
  private readonly scrubber: HTMLInputElement;
  private readonly timeEl: HTMLSpanElement;
  private readonly badgeEl: HTMLSpanElement;
  private readonly relativeEl: HTMLSpanElement;

  // Ruler layers (§A1)
  private readonly rulerBody: HTMLDivElement;
  private readonly trackLeftEl: HTMLDivElement;
  private readonly trackRightEl: HTMLDivElement;
  private readonly minorTicksEl: HTMLDivElement;
  private readonly majorTicksEl: HTMLDivElement;
  private readonly nowEl: HTMLDivElement;
  private readonly cursorEl: HTMLDivElement;
  private readonly cursorTooltipEl: HTMLSpanElement;
  private readonly loadingLabelEl: HTMLSpanElement;
  private readonly wrap: HTMLDivElement;

  private frames: number[] = [];
  private previousFrameSet = new Set<number>();
  private currentIndex = 0;
  private domainStart = 0;
  private domainEnd = 0;
  private domainLocked = false;
  private timezoneMode: TimezoneMode = "local";
  private speed: 1 | 2 = 1;
  private dragging = false;
  private disabled = true;
  private readonly compactMQ: MediaQueryList | null =
    typeof window.matchMedia === "function" ? window.matchMedia("(max-width: 360px)") : null;
  /** <640px: at most 3 major tick labels (§A8); ≤360px (compactMQ) narrows that further to edges only. */
  private readonly mobileMQ: MediaQueryList | null =
    typeof window.matchMedia === "function" ? window.matchMedia("(max-width: 639px)") : null;

  private readonly opts: PlaybackBarOptions;

  constructor(opts: PlaybackBarOptions) {
    this.opts = opts;

    this.element = document.createElement("div");
    this.element.className = "playback-bar card";
    this.element.hidden = true;

    const top = document.createElement("div");
    top.className = "playback-bar__top";

    this.playButton = document.createElement("button");
    this.playButton.type = "button";
    this.playButton.className = "playback-bar__play";
    this.playButton.setAttribute("aria-label", "Odtwórz animację");
    this.playButton.textContent = "▶";
    this.playButton.addEventListener("click", () => this.opts.onTogglePlay());

    this.speedButton = document.createElement("button");
    this.speedButton.type = "button";
    this.speedButton.className = "playback-bar__speed tabular-nums";
    this.speedButton.setAttribute("aria-pressed", "false");
    this.speedButton.addEventListener("click", () => this.opts.onToggleSpeed());

    const timeinfo = document.createElement("div");
    timeinfo.className = "playback-bar__timeinfo";

    const timeRow = document.createElement("div");
    this.timeEl = document.createElement("span");
    this.timeEl.className = "playback-bar__time tabular-nums";
    this.badgeEl = document.createElement("span");
    this.badgeEl.className = "playback-bar__badge";
    this.badgeEl.textContent = "TERAZ";
    this.badgeEl.hidden = true;
    timeRow.append(this.timeEl, this.badgeEl);

    this.relativeEl = document.createElement("span");
    this.relativeEl.className = "playback-bar__relative tabular-nums";

    timeinfo.append(timeRow, this.relativeEl);

    this.tzButton = document.createElement("button");
    this.tzButton.type = "button";
    this.tzButton.className = "playback-bar__tz";
    this.tzButton.setAttribute("aria-pressed", "false");
    this.tzButton.addEventListener("click", () => this.opts.onToggleTimezone());

    top.append(this.playButton, this.speedButton, timeinfo, this.tzButton);

    // ---- Ruler ----
    const scrubWrap = document.createElement("div");
    scrubWrap.className = "scrubber-wrap";
    this.wrap = scrubWrap;

    this.loadingLabelEl = document.createElement("span");
    this.loadingLabelEl.className = "ruler__loading-label tabular-nums";
    this.loadingLabelEl.hidden = true;

    this.rulerBody = document.createElement("div");
    this.rulerBody.className = "ruler__body";

    this.trackLeftEl = document.createElement("div");
    this.trackLeftEl.className = "ruler__track-seg ruler__track-seg--watched";
    this.trackRightEl = document.createElement("div");
    this.trackRightEl.className = "ruler__track-seg ruler__track-seg--unwatched";

    this.minorTicksEl = document.createElement("div");
    this.minorTicksEl.className = "ruler__ticks-minor";
    this.minorTicksEl.setAttribute("aria-hidden", "true");

    this.majorTicksEl = document.createElement("div");
    this.majorTicksEl.className = "ruler__ticks-major";
    this.majorTicksEl.setAttribute("aria-hidden", "true");

    this.nowEl = document.createElement("div");
    this.nowEl.className = "ruler__now";
    this.nowEl.setAttribute("aria-hidden", "true");
    const nowLabel = document.createElement("span");
    nowLabel.className = "ruler__now-label";
    nowLabel.textContent = "TERAZ";
    this.nowEl.appendChild(nowLabel);

    this.cursorEl = document.createElement("div");
    this.cursorEl.className = "ruler__cursor";
    this.cursorEl.setAttribute("aria-hidden", "true");
    this.cursorTooltipEl = document.createElement("span");
    this.cursorTooltipEl.className = "ruler__cursor-tooltip tabular-nums";
    this.cursorEl.appendChild(this.cursorTooltipEl);

    this.rulerBody.append(
      this.trackLeftEl,
      this.trackRightEl,
      this.minorTicksEl,
      this.majorTicksEl,
      this.nowEl,
      this.cursorEl
    );

    this.scrubber = document.createElement("input");
    this.scrubber.type = "range";
    this.scrubber.className = "scrubber";
    this.scrubber.min = "0";
    this.scrubber.max = "0";
    this.scrubber.step = "1";
    this.scrubber.value = "0";
    this.scrubber.setAttribute("aria-label", "Suwak czasu — wybór klatki radaru");
    this.scrubber.addEventListener("input", () => {
      this.opts.onScrub(Number(this.scrubber.value));
    });

    scrubWrap.append(this.loadingLabelEl, this.rulerBody, this.scrubber);
    this.element.append(top, scrubWrap);

    this.attachPointerHandlers(scrubWrap);
    this.compactMQ?.addEventListener?.("change", () => {
      if (this.frames.length > 0) this.setIndex(this.currentIndex);
      this.renderMajorTicks();
    });
    this.mobileMQ?.addEventListener?.("change", () => this.renderMajorTicks());
    window.addEventListener("resize", () => this.renderMajorTicks());
  }

  // ---- Pointer interaction: x → time → nearest verified frame (§A1) ----
  private attachPointerHandlers(wrap: HTMLElement): void {
    const moveTo = (clientX: number): void => {
      if (this.disabled || this.frames.length === 0) return;
      const rect = this.rulerBody.getBoundingClientRect();
      if (rect.width <= 0) return;
      const frac = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const targetMs = this.domainStart + frac * (this.domainEnd - this.domainStart);
      const idx = nearestFrameIndexByTime(this.frames, targetMs);
      if (idx >= 0) this.opts.onScrub(idx);
    };

    wrap.addEventListener("pointerdown", (e) => {
      if (this.disabled) return;
      this.dragging = true;
      this.rulerBody.classList.add("is-dragging");
      try {
        wrap.setPointerCapture(e.pointerId);
      } catch {
        /* ignore: some environments (e.g. tests) lack full Pointer Events support */
      }
      moveTo(e.clientX);
    });
    wrap.addEventListener("pointermove", (e) => {
      if (!this.dragging) return;
      moveTo(e.clientX);
    });
    const endDrag = (): void => {
      if (!this.dragging) return;
      this.dragging = false;
      this.rulerBody.classList.remove("is-dragging");
    };
    wrap.addEventListener("pointerup", endDrag);
    wrap.addEventListener("pointercancel", endDrag);
  }

  private isCompact(): boolean {
    return this.compactMQ?.matches ?? false;
  }

  /**
   * Fix the ruler's time domain (§A7: "Domena toru ustalana od razu na
   * docelowe okno"). Call once, before progressive loading begins, with the
   * full target window's oldest/newest advertised times — subsequent
   * `setFrames` calls then only add ticks inside this fixed range instead of
   * shifting it. Call `releaseDomainLock()` once loading is fully complete
   * so later polls can slide the window forward as usual.
   */
  setDomain(startMs: number, endMs: number): void {
    this.domainStart = startMs;
    this.domainEnd = endMs;
    this.domainLocked = true;
    this.renderMajorTicks();
    this.renderMinorTicks();
    this.renderNow();
  }

  /** Let the domain slide again with each new/rotated frame (post progressive-load steady state). */
  releaseDomainLock(): void {
    this.domainLocked = false;
  }

  /** Progressive-loading micro-label (§A7 "WCZYTYWANIE X/N"); hidden once `active` is false. */
  setLoadingProgress(done: number, total: number, active: boolean): void {
    this.loadingLabelEl.hidden = !active || total <= 0;
    if (active && total > 0) {
      this.loadingLabelEl.textContent = `WCZYTYWANIE ${done}/${total}`;
    }
  }

  /** (Re)initialize the ruler for a new/updated frame window (ascending oldest → newest). */
  setFrames(frameTimes: number[]): void {
    this.frames = frameTimes;
    const count = frameTimes.length;
    this.scrubber.max = String(Math.max(0, count - 1));

    if (!this.domainLocked && count > 0) {
      this.domainStart = frameTimes[0];
      this.domainEnd = frameTimes[count - 1];
    }

    this.renderMinorTicks();
    this.renderMajorTicks();
    this.renderNow();
  }

  setIndex(index: number): void {
    this.currentIndex = index;
    this.scrubber.value = String(index);
    const frameMs = this.frames[index];
    if (frameMs === undefined) return;
    const newestMs = this.frames[this.frames.length - 1];
    const isNewest = frameMs === newestMs;

    const clockLabel = formatTimeForMode(frameMs, this.timezoneMode);
    this.timeEl.textContent = clockLabel;
    const tooltip = formatOppositeZoneTooltip(frameMs, this.timezoneMode);
    this.timeEl.title = tooltip;
    this.scrubber.title = `${clockLabel} (${tooltip})`;

    const minutesBefore = Math.max(0, Math.round((newestMs - frameMs) / 60_000));
    if (isNewest) {
      this.badgeEl.hidden = false;
      this.relativeEl.textContent = "";
    } else {
      this.badgeEl.hidden = true;
      this.relativeEl.textContent = formatHistoryLabel(minutesBefore, this.isCompact());
    }

    this.scrubber.setAttribute("aria-valuetext", formatScrubberValueText(clockLabel, minutesBefore));

    const frac = fracOfTime(frameMs, this.domainStart, this.domainEnd);
    this.cursorEl.style.left = `${frac * 100}%`;
    this.cursorTooltipEl.textContent = clockLabel;
    this.trackLeftEl.style.width = `${frac * 100}%`;
    this.trackRightEl.style.left = `${frac * 100}%`;
    this.trackRightEl.style.width = `${(1 - frac) * 100}%`;
    this.trackRightEl.classList.toggle("is-newest", isNewest);
  }

  setPlaying(playing: boolean): void {
    this.playButton.textContent = playing ? "⏸" : "▶";
    this.playButton.setAttribute("aria-label", playing ? "Wstrzymaj animację" : "Odtwórz animację");
  }

  /** Playback speed toggle state (§A4); caller (app.ts) owns the persisted value. */
  setSpeed(multiplier: 1 | 2): void {
    this.speed = multiplier;
    this.speedButton.textContent = multiplier === 2 ? "2×" : "1×";
    this.speedButton.setAttribute("aria-pressed", String(multiplier === 2));
    this.speedButton.setAttribute(
      "aria-label",
      multiplier === 2
        ? "Prędkość odtwarzania 2 razy — kliknij, aby zmienić na 1 raz"
        : "Prędkość odtwarzania 1 raz — kliknij, aby zmienić na 2 razy"
    );
  }

  getSpeed(): 1 | 2 {
    return this.speed;
  }

  /** Timezone toggle state (§A4): switches every displayed time, including ruler major ticks. */
  setTimezoneMode(mode: TimezoneMode): void {
    this.timezoneMode = mode;
    this.tzButton.textContent = mode === "utc" ? "UTC" : "LOK";
    this.tzButton.setAttribute("aria-pressed", String(mode === "utc"));
    this.tzButton.setAttribute(
      "aria-label",
      mode === "utc"
        ? "Strefa czasowa: UTC — kliknij, aby przełączyć na czas lokalny"
        : "Strefa czasowa: lokalna — kliknij, aby przełączyć na UTC"
    );
    this.renderMajorTicks();
    if (this.frames.length > 0) this.setIndex(this.currentIndex);
  }

  setDisabled(disabled: boolean): void {
    this.disabled = disabled;
    this.playButton.disabled = disabled;
    this.scrubber.disabled = disabled;
    this.speedButton.disabled = disabled;
    this.tzButton.disabled = disabled;
    this.wrap.classList.toggle("is-disabled", disabled);
  }

  show(): void {
    this.element.hidden = false;
    this.renderMajorTicks();
  }

  hide(): void {
    this.element.hidden = true;
  }

  // ---- Rendering ----

  private renderMinorTicks(): void {
    this.minorTicksEl.innerHTML = "";
    for (const t of this.frames) {
      const span = document.createElement("span");
      span.className = "ruler__tick-minor";
      if (!this.previousFrameSet.has(t)) span.classList.add("is-new");
      span.style.left = `${fracOfTime(t, this.domainStart, this.domainEnd) * 100}%`;
      this.minorTicksEl.appendChild(span);
    }
    this.previousFrameSet = new Set(this.frames);
  }

  /**
   * At <640px keep at most 3 major-tick labels (first/middle/last); at
   * ≤360px (compactMQ) narrow that to edges only (§A8). Tick *marks* are
   * always drawn for every major tick — only their labels are thinned.
   */
  private applyResponsiveLabelLimit(positioned: PositionedTick[]): PositionedTick[] {
    const maxLabels = this.isCompact() ? 2 : this.mobileMQ?.matches ? 3 : Infinity;
    const labeledIdx = positioned
      .map((t, i) => (t.showLabel ? i : -1))
      .filter((i) => i >= 0);
    if (labeledIdx.length <= maxLabels) return positioned;

    const keep = new Set<number>([labeledIdx[0], labeledIdx[labeledIdx.length - 1]]);
    if (maxLabels >= 3) {
      keep.add(labeledIdx[Math.floor((labeledIdx.length - 1) / 2)]);
    }
    return positioned.map((t, i) => (keep.has(i) ? t : { ...t, showLabel: false }));
  }

  private renderMajorTicks(): void {
    if (this.domainEnd <= this.domainStart) {
      this.majorTicksEl.innerHTML = "";
      return;
    }
    const offsetMinutes = timezoneOffsetMinutesForMode(this.timezoneMode);
    const raw = computeMajorTicks(this.domainStart, this.domainEnd, offsetMinutes);
    const width = this.rulerBody.clientWidth;
    const positioned = this.applyResponsiveLabelLimit(
      positionMajorTicks(raw, this.domainStart, this.domainEnd, width)
    );

    this.majorTicksEl.innerHTML = "";
    for (const t of positioned) {
      const mark = document.createElement("span");
      mark.className = "ruler__tick-major";
      mark.style.left = `${t.frac * 100}%`;

      const line = document.createElement("span");
      line.className = "ruler__tick-major-line";
      mark.appendChild(line);

      if (t.showLabel) {
        const label = document.createElement("span");
        label.className = "ruler__tick-major-label tabular-nums";
        label.textContent = t.label;
        mark.appendChild(label);
      }
      this.majorTicksEl.appendChild(mark);
    }
  }

  private renderNow(): void {
    if (this.frames.length === 0) {
      this.nowEl.style.display = "none";
      return;
    }
    this.nowEl.style.display = "";
    const newestMs = this.frames[this.frames.length - 1];
    const frac = fracOfTime(newestMs, this.domainStart, this.domainEnd);
    this.nowEl.style.left = `${frac * 100}%`;
  }
}
